import { supabase } from '@/integrations/supabase/client';
import { Teacher, TeacherDepartment, TeacherDocument, TeacherPayment } from '@/types/teacher.types';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

// Type aliases for better readability
type SupabaseTeacher = Database['public']['Tables']['teachers']['Row'];
type SupabaseTeacherInsert = Database['public']['Tables']['teachers']['Insert'];
type SupabaseTeacherDepartment = Database['public']['Tables']['teacher_departments']['Row'];
type SupabaseTeacherSubject = Database['public']['Tables']['teacher_subjects']['Row'];
type SupabaseTeacherDocument = Database['public']['Tables']['teacher_documents']['Row'];
type SupabaseTeacherPayment = Database['public']['Tables']['teacher_payments']['Row'];

class TeacherSupabaseService {
  private isOnline = navigator.onLine;

  constructor() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncPendingChanges();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  private convertToLocalTeacher(
    supabaseTeacher: SupabaseTeacher,
    departments: SupabaseTeacherDepartment[] = [],
    subjects: SupabaseTeacherSubject[] = [],
    documents: SupabaseTeacherDocument[] = [],
    payments: SupabaseTeacherPayment[] = []
  ): Teacher {
    return {
      id: supabaseTeacher.id,
      firstName: supabaseTeacher.first_name,
      lastName: supabaseTeacher.last_name,
      fatherName: supabaseTeacher.father_name || '',
      gender: supabaseTeacher.gender || '',
      position: supabaseTeacher.position || '',
      idNumber: supabaseTeacher.id_number || '',
      email: supabaseTeacher.email || '',
      phone: supabaseTeacher.phone || '',
      country: supabaseTeacher.country || 'Afghanistan',
      city: supabaseTeacher.city || '',
      address: supabaseTeacher.address || '',
      age: supabaseTeacher.age || '',
      dateOfBirth: supabaseTeacher.date_of_birth || '',
      maritalStatus: supabaseTeacher.marital_status || '',
      joinDate: supabaseTeacher.join_date,
      contractEndDate: supabaseTeacher.contract_end_date || '',
      salary: supabaseTeacher.salary?.toString() || '',
      paymentStatus: supabaseTeacher.payment_status || 'pending',
      employmentType: supabaseTeacher.employment_type || 'full_time',
      status: supabaseTeacher.status || 'active',
      profileImage: supabaseTeacher.profile_image || '',
      bio: supabaseTeacher.bio || '',
      departments: departments.map(d => ({
        id: d.id,
        name: d.name,
        subject: d.subject || '',
        classType: d.class_type || '',
        startTime: d.start_time || '',
        endTime: d.end_time || '',
        days: d.days || []
      })),
      subjects: subjects.map(s => s.subject),
      qualifications: [], // Will be loaded separately if needed
      documents: documents.map(d => ({
        id: d.id,
        name: d.name,
        size: d.size,
        type: d.type,
        url: d.url,
        documentType: d.document_type as any,
        dateAdded: d.date_added,
        expirationDate: d.expiration_date,
        status: d.status as any,
        metadata: (d.metadata as any) || {} // Cast Json to expected type
      })),
      payments: payments.map(p => ({
        id: p.id,
        amount: p.amount,
        paymentDate: p.payment_date,
        paymentPeriod: p.payment_period || '',
        status: p.status as any,
        method: p.method || '',
        reference: p.reference || '',
        notes: p.notes || '',
        allowances: Array.isArray(p.allowances) ? p.allowances as Array<{ type: string; amount: number }> : [],
        deductions: Array.isArray(p.deductions) ? p.deductions as Array<{ type: string; amount: number }> : [],
        createdAt: p.created_at || new Date().toISOString()
      })),
      history: [], // Will be handled separately
      createdAt: supabaseTeacher.created_at || new Date().toISOString(),
      updatedAt: supabaseTeacher.updated_at || new Date().toISOString()
    };
  }

  private convertToSupabaseTeacher(teacher: Teacher): SupabaseTeacherInsert {
    // Validate and convert employment_type to match Supabase enum
    const validEmploymentTypes = ['full_time', 'part_time', 'contract', 'temporary'] as const;
    const employmentType = validEmploymentTypes.includes(teacher.employmentType as any) 
      ? teacher.employmentType as Database['public']['Enums']['teacher_employment_type']
      : 'full_time';

    // Validate and convert status to match Supabase enum
    const validStatuses = ['active', 'inactive', 'on_leave', 'terminated'] as const;
    const status = validStatuses.includes(teacher.status as any)
      ? teacher.status as Database['public']['Enums']['teacher_status']
      : 'active';

    // Validate and convert payment_status to match Supabase enum
    const validPaymentStatuses = ['paid', 'pending', 'partial', 'overdue'] as const;
    const paymentStatus = validPaymentStatuses.includes(teacher.paymentStatus as any)
      ? teacher.paymentStatus as Database['public']['Enums']['teacher_payment_status']
      : 'pending';

    return {
      id: teacher.id,
      first_name: teacher.firstName,
      last_name: teacher.lastName,
      father_name: teacher.fatherName || null,
      gender: teacher.gender || null,
      position: teacher.position || null,
      id_number: teacher.idNumber || null,
      email: teacher.email || null,
      phone: teacher.phone || null,
      country: teacher.country || 'Afghanistan',
      city: teacher.city || null,
      address: teacher.address || null,
      age: teacher.age || null,
      date_of_birth: teacher.dateOfBirth || null,
      marital_status: teacher.maritalStatus || null,
      join_date: teacher.joinDate,
      contract_end_date: teacher.contractEndDate || null,
      salary: teacher.salary ? parseFloat(teacher.salary) : null,
      payment_status: paymentStatus,
      employment_type: employmentType,
      status: status,
      profile_image: teacher.profileImage || null,
      bio: teacher.bio || null,
      created_at: teacher.createdAt,
      updated_at: teacher.updatedAt
    };
  }

  async syncAllTeachers(): Promise<Teacher[]> {
    if (!this.isOnline) {
      return this.getLocalTeachers();
    }

    try {
      // Fetch teachers from Supabase
      const { data: teachers, error: teachersError } = await supabase
        .from('teachers')
        .select('*');

      if (teachersError) {
        console.error('Error fetching teachers:', teachersError);
        return this.getLocalTeachers();
      }

      // Fetch related data
      const { data: departments } = await supabase
        .from('teacher_departments')
        .select('*');

      const { data: subjects } = await supabase
        .from('teacher_subjects')
        .select('*');

      const { data: documents } = await supabase
        .from('teacher_documents')
        .select('*');

      const { data: payments } = await supabase
        .from('teacher_payments')
        .select('*');

      // Convert and group data
      const convertedTeachers: Teacher[] = (teachers || []).map(teacher => {
        const teacherDepartments = (departments || []).filter(d => d.teacher_id === teacher.id);
        const teacherSubjects = (subjects || []).filter(s => s.teacher_id === teacher.id);
        const teacherDocuments = (documents || []).filter(d => d.teacher_id === teacher.id);
        const teacherPayments = (payments || []).filter(p => p.teacher_id === teacher.id);
        
        return this.convertToLocalTeacher(
          teacher,
          teacherDepartments,
          teacherSubjects,
          teacherDocuments,
          teacherPayments
        );
      });

      // Update localStorage with synced data
      if (convertedTeachers.length > 0) {
        localStorage.setItem('nalc_teachers', JSON.stringify(convertedTeachers));
      }

      return convertedTeachers;
    } catch (error) {
      console.error('Sync error:', error);
      return this.getLocalTeachers();
    }
  }

  private getLocalTeachers(): Teacher[] {
    try {
      const stored = localStorage.getItem('nalc_teachers');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading local teachers:', error);
      return [];
    }
  }

  async saveTeacher(teacher: Teacher): Promise<Teacher> {
    // Always save to localStorage first
    const localTeachers = this.getLocalTeachers();
    const existingIndex = localTeachers.findIndex(t => t.id === teacher.id);
    
    if (existingIndex >= 0) {
      localTeachers[existingIndex] = teacher;
    } else {
      localTeachers.push(teacher);
    }
    
    localStorage.setItem('nalc_teachers', JSON.stringify(localTeachers));

    // Background sync to Supabase if online
    if (this.isOnline) {
      this.backgroundSyncTeacher(teacher);
    } else {
      this.queueForSync(teacher);
    }

    return teacher;
  }

  private async backgroundSyncTeacher(teacher: Teacher): Promise<void> {
    try {
      const supabaseTeacher = this.convertToSupabaseTeacher(teacher);
      
      // Upsert teacher - fix the call by wrapping in array for batch upsert
      const { error: teacherError } = await supabase
        .from('teachers')
        .upsert([supabaseTeacher]); // Wrap in array

      if (teacherError) {
        console.error('Error syncing teacher:', teacherError);
        this.queueForSync(teacher);
        return;
      }

      // Sync departments
      if (teacher.departments && teacher.departments.length > 0) {
        // Delete existing departments
        await supabase
          .from('teacher_departments')
          .delete()
          .eq('teacher_id', teacher.id);

        // Insert new departments
        const departmentsToInsert = teacher.departments.map(dept => ({
          id: dept.id,
          teacher_id: teacher.id,
          name: dept.name,
          subject: dept.subject,
          class_type: dept.classType,
          start_time: dept.startTime,
          end_time: dept.endTime,
          days: dept.days
        }));

        const { error: deptError } = await supabase
          .from('teacher_departments')
          .insert(departmentsToInsert);

        if (deptError) {
          console.error('Error syncing departments:', deptError);
        }
      }

      // Sync subjects
      if (teacher.subjects && teacher.subjects.length > 0) {
        // Delete existing subjects
        await supabase
          .from('teacher_subjects')
          .delete()
          .eq('teacher_id', teacher.id);

        // Insert new subjects
        const subjectsToInsert = teacher.subjects.map(subject => ({
          teacher_id: teacher.id,
          subject: subject
        }));

        const { error: subjectError } = await supabase
          .from('teacher_subjects')
          .insert(subjectsToInsert);

        if (subjectError) {
          console.error('Error syncing subjects:', subjectError);
        }
      }

      // Sync documents
      if (teacher.documents && teacher.documents.length > 0) {
        // Delete existing documents
        await supabase
          .from('teacher_documents')
          .delete()
          .eq('teacher_id', teacher.id);

        // Insert new documents
        const documentsToInsert = teacher.documents.map(doc => ({
          id: doc.id,
          teacher_id: teacher.id,
          name: doc.name,
          size: doc.size,
          type: doc.type,
          url: doc.url,
          document_type: doc.documentType,
          date_added: doc.dateAdded,
          expiration_date: doc.expirationDate,
          status: doc.status,
          metadata: doc.metadata
        }));

        const { error: docError } = await supabase
          .from('teacher_documents')
          .insert(documentsToInsert);

        if (docError) {
          console.error('Error syncing documents:', docError);
        }
      }

      console.log('Teacher synced to Supabase:', teacher.id);
    } catch (error) {
      console.error('Background sync error:', error);
      this.queueForSync(teacher);
    }
  }

  private queueForSync(teacher: Teacher): void {
    const queue = JSON.parse(localStorage.getItem('supabase_teacher_sync_queue') || '[]');
    const existingIndex = queue.findIndex((q: any) => q.id === teacher.id);
    
    if (existingIndex >= 0) {
      queue[existingIndex] = teacher;
    } else {
      queue.push(teacher);
    }
    
    localStorage.setItem('supabase_teacher_sync_queue', JSON.stringify(queue));
  }

  private async syncPendingChanges(): Promise<void> {
    const queue = JSON.parse(localStorage.getItem('supabase_teacher_sync_queue') || '[]');
    
    if (queue.length === 0) return;

    console.log(`Syncing ${queue.length} pending teacher changes...`);
    
    for (const teacher of queue) {
      await this.backgroundSyncTeacher(teacher);
    }
    
    // Clear the queue after successful sync
    localStorage.removeItem('supabase_teacher_sync_queue');
    toast.success('All teacher changes synced to cloud');
  }

  async deleteTeacher(id: string): Promise<void> {
    // Remove from localStorage
    const localTeachers = this.getLocalTeachers();
    const filteredTeachers = localTeachers.filter(t => t.id !== id);
    localStorage.setItem('nalc_teachers', JSON.stringify(filteredTeachers));

    // Background delete from Supabase
    if (this.isOnline) {
      try {
        const { error } = await supabase
          .from('teachers')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('Error deleting from Supabase:', error);
        }
      } catch (error) {
        console.error('Delete sync error:', error);
      }
    }
  }

  getSyncStatus(): 'synced' | 'pending' | 'offline' {
    if (!this.isOnline) return 'offline';
    
    const queue = JSON.parse(localStorage.getItem('supabase_teacher_sync_queue') || '[]');
    return queue.length > 0 ? 'pending' : 'synced';
  }
}

export const teacherSupabaseService = new TeacherSupabaseService();
